import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Collapse,
  Typography,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Tooltip,
  IconButton,
  Slider,
  Button,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import type { CameraMarkerSize } from '@signals/app-shell/hooks/useCameraLocationsOverlay';
import {
  Close as CloseIcon,
  ExpandLess as CollapseIcon,
  ExpandMore as ExpandIcon,
  Info as InfoIcon,
  RestartAlt as ResetIcon,
} from '@mui/icons-material';
import ReferenceLayersSection from '@signals/app-shell/components/map/ReferenceLayersSection';
import { useReferenceLayers } from '@signals/app-shell/hooks/useReferenceLayers';
import { useAuthStore } from '@signals/app-shell/stores';
import {
  CameraLayerFilterPanel,
  CameraProximityRow,
  type CameraLayerFilterOverlay,
  type CameraProximityControl,
} from '@signals/app-shell/components/CameraLayerFilterPopover';

/** Camera filter state published by PathfinderMapGL (it owns the overlay hook). */
export type PathfinderCameraControls = {
  overlay: CameraLayerFilterOverlay;
  proximity: CameraProximityControl;
  /** Cameras drawn after category / source / near-track filtering. */
  shownCount: number;
};

/**
 * Centered popover-style dialog that owns ALL of the user-tweakable map
 * controls in one place: Primary Layers, Co-Traveler overlays, White Cell
 * sampling, and Playback Settings (Unit / Interval / Rate / Loop /
 * Follow / Skip Dwell / GPS Gaps).
 *
 * History / why one dialog instead of multiple:
 *   1. These toggles originally lived inside `CompactTimeSlider`'s
 *      collapsible drawer. Opening it grew the slider's height ~6x and
 *      resized the map column, forcing Mapbox to repaint - the "white
 *      flash" users complained about.
 *   2. Splitting them into TWO floating panels (one Popover for playback
 *      anchored to the slider gear, one Dialog for layers anchored to the
 *      map toolbar) fixed the resize problem, but the popover obscured
 *      its own anchor button, so users couldn't click the gear again to
 *      close it - they had to clickaway elsewhere.
 *   3. Consolidating everything into this single centered dialog gives a
 *      consistent close mechanism (X button, backdrop click, Escape) and
 *      keeps both entry points - the slider gear AND the map toolbar
 *      "Map Layers" button - opening the same panel.
 *
 * The dialog floats *over* the map, so neither the slider nor the map
 * column changes size when it opens or closes.
 */

export interface MapLayerControlsDialogProps {
  open: boolean;
  onClose: () => void;

  // Primary Layers
  showDataPoints?: boolean;
  onShowDataPointsChange?: (show: boolean) => void;
  showRouteLines?: boolean;
  onShowRouteLinesChange?: (show: boolean) => void;
  showDirectionArrows?: boolean;
  onShowDirectionArrowsChange?: (show: boolean) => void;
  showHeatmap?: boolean;
  onShowHeatmapChange?: (show: boolean) => void;
  enableClustering?: boolean;
  onEnableClusteringChange?: (enable: boolean) => void;
  showDwells?: boolean;
  onShowDwellsChange?: (show: boolean) => void;
  showPttMessages?: boolean;
  onShowPttMessagesChange?: (show: boolean) => void;
  clusterPttMessages?: boolean;
  onClusterPttMessagesChange?: (cluster: boolean) => void;

  // Dwell centre labels (duration text) — toggled here, rendered by the map
  showDwellLabels?: boolean;
  onShowDwellLabelsChange?: (show: boolean) => void;
  dwellCount?: number;

  // Student itinerary points
  showItinerary?: boolean;
  onShowItineraryChange?: (show: boolean) => void;
  hasItineraryData?: boolean;

  // Camera locations overlay + its category / source / near-track filters
  showCameras?: boolean;
  onShowCamerasChange?: (show: boolean) => void;
  cameraCount?: number;
  cameraControls?: PathfinderCameraControls | null;

  // Admin reference layers (boundaries, districts, POIs…). The section is
  // always rendered — the hook is a shared store, so the map and this dialog
  // see the same enabled set and style overrides.
  onZoomToBounds?: (bbox: [number, number, number, number]) => void;

  // Co-Traveler overlays
  showCoTravelerPoints?: boolean;
  onShowCoTravelerPointsChange?: (show: boolean) => void;
  showCoTravelerLines?: boolean;
  onShowCoTravelerLinesChange?: (show: boolean) => void;
  showCoTravelerHeatmap?: boolean;
  onShowCoTravelerHeatmapChange?: (show: boolean) => void;
  highlightClosestSrp?: boolean;
  onHighlightClosestSrpChange?: (show: boolean) => void;

  // White Cell sampling — show one GPS point per interval (in minutes)
  whiteCellInterval?: number | null;
  onWhiteCellIntervalChange?: (interval: number | null) => void;

  // Playback Settings (slider gear used to own these in a popover)
  timeUnit?: string;
  onTimeUnitChange?: (unit: string) => void;
  intervalValue?: number;
  onIntervalValueChange?: (value: number) => void;
  playRate?: number;
  onPlayRateChange?: (rate: number) => void;
  loop?: boolean;
  onLoopChange?: (loop: boolean) => void;
  followMode?: boolean;
  onFollowModeChange?: (follow: boolean) => void;
  skipDwells?: boolean;
  onSkipDwellsChange?: (skip: boolean) => void;
  hideGaps?: boolean;
  onHideGapsChange?: (hide: boolean) => void;
  hideImplausibleMovement?: boolean;
  onHideImplausibleMovementChange?: (hide: boolean) => void;
  implausibleMaxSpeedKmh?: number;
  onImplausibleMaxSpeedKmhChange?: (kmh: number) => void;
  onUseOrgTrackFilterDefaults?: () => void;
  trackFiltersUserCustomized?: boolean;
  /** Intel Packet only needs playback — hide Pathfinder-specific layer toggles. */
  playbackOnly?: boolean;
}

// Module-level constants mirrored from CompactTimeSlider so the dialog can
// render the Unit / Interval / Rate dropdowns without importing from a
// peer component (avoids a circular dep risk).
const TIME_UNITS = [
  { value: 'seconds', label: 'Seconds' },
  { value: 'minutes', label: 'Minutes' },
  { value: 'hours', label: 'Hours' },
];
const INTERVAL_OPTIONS = Array.from({ length: 30 }, (_, i) => i + 1);
const PLAY_RATES = [0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0, 1.5, 2.0, 2.5, 3.0];

const MapLayerControlsDialog: React.FC<MapLayerControlsDialogProps> = ({
  open,
  onClose,
  showDataPoints = true,
  onShowDataPointsChange,
  showRouteLines = true,
  onShowRouteLinesChange,
  showDirectionArrows = false,
  onShowDirectionArrowsChange,
  showHeatmap = false,
  onShowHeatmapChange,
  enableClustering = false,
  onEnableClusteringChange,
  showDwells = true,
  onShowDwellsChange,
  showPttMessages = true,
  onShowPttMessagesChange,
  clusterPttMessages = true,
  onClusterPttMessagesChange,
  showDwellLabels = false,
  onShowDwellLabelsChange,
  dwellCount,
  showItinerary = false,
  onShowItineraryChange,
  hasItineraryData = false,
  showCameras = false,
  onShowCamerasChange,
  cameraCount,
  cameraControls,
  onZoomToBounds,
  showCoTravelerPoints = false,
  onShowCoTravelerPointsChange,
  showCoTravelerLines = false,
  onShowCoTravelerLinesChange,
  showCoTravelerHeatmap = false,
  onShowCoTravelerHeatmapChange,
  highlightClosestSrp = false,
  onHighlightClosestSrpChange,
  whiteCellInterval = null,
  onWhiteCellIntervalChange,
  timeUnit = 'seconds',
  onTimeUnitChange,
  intervalValue = 30,
  onIntervalValueChange,
  playRate = 0.5,
  onPlayRateChange,
  loop = true,
  onLoopChange,
  followMode = false,
  onFollowModeChange,
  skipDwells = false,
  onSkipDwellsChange,
  hideGaps = false,
  onHideGapsChange,
  hideImplausibleMovement = true,
  onHideImplausibleMovementChange,
  implausibleMaxSpeedKmh = 160,
  onImplausibleMaxSpeedKmhChange,
  onUseOrgTrackFilterDefaults,
  trackFiltersUserCustomized = false,
  playbackOnly = false,
}) => {
  const mph = Math.round(implausibleMaxSpeedKmh * 0.621371);
  // Only fetch the layer list while the dialog is open (the map keeps its own
  // subscription alive for whatever is already switched on).
  const referenceLayers = useReferenceLayers(open);
  const isAdmin = useAuthStore((s) => Boolean(s.user?.roles?.includes('administrator')));
  const [cameraFiltersOpen, setCameraFiltersOpen] = useState(false);
  const [referenceLayersOpen, setReferenceLayersOpen] = useState(false);
  const cameraFiltersActive = Boolean(
    cameraControls && (cameraControls.overlay.hasActiveFilters || cameraControls.proximity.prefs.enabled)
  );
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      // Slightly translucent backdrop so the map is still readable while
      // the user toggles layers - they can see the camera underneath, but
      // it's dimmed enough that the dialog itself reads as primary.
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.35)',
            backdropFilter: 'blur(2px)',
          },
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: 2,
          // Disable MUI's elevation gradient overlay so the dialog has a
          // single, predictable background color in both light and dark
          // themes - matters for the slightly translucent look.
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 1.25,
          px: 2,
          fontSize: '1rem',
          fontWeight: 600,
        }}
      >
        Map Settings
        <IconButton onClick={onClose} size="small" aria-label="Close map settings">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ px: 2, py: 1.5 }}>
        {!playbackOnly && (
        <>
        {/* Primary Layers */}
        <Box sx={{ mb: 1.25 }}>
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5, letterSpacing: 0.4 }}
          >
            PRIMARY LAYERS
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
              columnGap: 1,
              rowGap: 0.25,
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={showDataPoints}
                  onChange={(e) => onShowDataPointsChange?.(e.target.checked)}
                  size="small"
                />
              }
              label={<Typography variant="caption">Points</Typography>}
              sx={{ m: 0 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={showRouteLines}
                  onChange={(e) => onShowRouteLinesChange?.(e.target.checked)}
                  size="small"
                />
              }
              label={<Typography variant="caption">Routes</Typography>}
              sx={{ m: 0 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={showDirectionArrows}
                  onChange={(e) => onShowDirectionArrowsChange?.(e.target.checked)}
                  size="small"
                  disabled={!showRouteLines}
                />
              }
              label={<Typography variant="caption">Arrows</Typography>}
              sx={{ m: 0 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={showHeatmap}
                  onChange={(e) => onShowHeatmapChange?.(e.target.checked)}
                  size="small"
                />
              }
              label={<Typography variant="caption">Heatmap</Typography>}
              sx={{ m: 0 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={enableClustering}
                  onChange={(e) => onEnableClusteringChange?.(e.target.checked)}
                  size="small"
                  disabled={!showDataPoints}
                />
              }
              label={<Typography variant="caption">Cluster</Typography>}
              sx={{ m: 0 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={showDwells}
                  onChange={(e) => onShowDwellsChange?.(e.target.checked)}
                  size="small"
                />
              }
              label={<Typography variant="caption">Dwells</Typography>}
              sx={{ m: 0 }}
            />
            {onShowPttMessagesChange && (
              <FormControlLabel
                control={
                  <Switch
                    checked={showPttMessages}
                    onChange={(e) => onShowPttMessagesChange(e.target.checked)}
                    size="small"
                  />
                }
                label={<Typography variant="caption">PTT</Typography>}
                sx={{ m: 0 }}
              />
            )}
            {onClusterPttMessagesChange && (
              <FormControlLabel
                control={
                  <Switch
                    checked={clusterPttMessages}
                    onChange={(e) => onClusterPttMessagesChange(e.target.checked)}
                    size="small"
                    disabled={!showPttMessages}
                  />
                }
                label={<Typography variant="caption">PTT Cluster</Typography>}
                sx={{ m: 0 }}
              />
            )}
            {onShowDwellLabelsChange && (
              <Tooltip title="Centre dot + duration text on each dwell" placement="top" arrow>
                <FormControlLabel
                  control={
                    <Switch
                      checked={showDwellLabels}
                      onChange={(e) => onShowDwellLabelsChange(e.target.checked)}
                      size="small"
                      disabled={!showDwells || dwellCount === 0}
                    />
                  }
                  label={<Typography variant="caption">Dwell labels</Typography>}
                  sx={{ m: 0 }}
                />
              </Tooltip>
            )}
            {onShowItineraryChange && (
              <Tooltip
                title={hasItineraryData ? 'Student itinerary points for the current selection' : 'No student itinerary data for the current selection'}
                placement="top"
                arrow
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={showItinerary}
                      onChange={(e) => onShowItineraryChange(e.target.checked)}
                      size="small"
                      disabled={!hasItineraryData}
                    />
                  }
                  label={<Typography variant="caption">Itinerary</Typography>}
                  sx={{ m: 0 }}
                />
              </Tooltip>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 1 }} />

        {/* Co-Traveler Layers */}
        <Box sx={{ mb: 1.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.4 }}
            >
              CO-TRAVELER
            </Typography>
            <Tooltip
              title="Co-Traveler Points/Lines: Full track history of identified co-travelers. Co-Traveler Heatmap: Shows WHERE encounters occurred. Closest SRP: amber ring on the SRP nearest the selected student."
              arrow
              placement="top"
            >
              <InfoIcon sx={{ fontSize: 12, color: 'text.disabled', cursor: 'help' }} />
            </Tooltip>
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
              columnGap: 1,
              rowGap: 0.25,
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={showCoTravelerPoints}
                  onChange={(e) => onShowCoTravelerPointsChange?.(e.target.checked)}
                  size="small"
                />
              }
              label={<Typography variant="caption">Points</Typography>}
              sx={{ m: 0 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={showCoTravelerLines}
                  onChange={(e) => onShowCoTravelerLinesChange?.(e.target.checked)}
                  size="small"
                />
              }
              label={<Typography variant="caption">Lines</Typography>}
              sx={{ m: 0 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={showCoTravelerHeatmap}
                  onChange={(e) => onShowCoTravelerHeatmapChange?.(e.target.checked)}
                  size="small"
                />
              }
              label={<Typography variant="caption">Heatmap</Typography>}
              sx={{ m: 0 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={highlightClosestSrp}
                  onChange={(e) => onHighlightClosestSrpChange?.(e.target.checked)}
                  size="small"
                />
              }
              label={<Typography variant="caption">Closest SRP</Typography>}
              sx={{ m: 0 }}
            />
          </Box>
        </Box>

        {onShowCamerasChange && (
          <>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ mb: 1.25 }}>
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5, letterSpacing: 0.4 }}
              >
                CAMERAS
                {showCameras && cameraCount != null && cameraCount > 0 && (
                  <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 0.5, fontWeight: 400 }}>
                    ({cameraCount.toLocaleString()})
                  </Typography>
                )}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1, rowGap: 0.25 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={showCameras}
                      onChange={(e) => onShowCamerasChange(e.target.checked)}
                      size="small"
                    />
                  }
                  label={<Typography variant="caption">Cameras</Typography>}
                  sx={{ m: 0 }}
                />
                {cameraControls && (
                  <>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={cameraControls.overlay.display.cluster}
                          onChange={(e) => cameraControls.overlay.setDisplay({ cluster: e.target.checked })}
                          size="small"
                          disabled={!showCameras}
                        />
                      }
                      label={<Typography variant="caption">Cluster</Typography>}
                      sx={{ m: 0 }}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={cameraControls.overlay.display.labels}
                          onChange={(e) => cameraControls.overlay.setDisplay({ labels: e.target.checked })}
                          size="small"
                          disabled={!showCameras}
                        />
                      }
                      label={
                        <Tooltip title="Camera names under markers from zoom 13 (clusters open up earlier while this is on)" placement="top" arrow>
                          <Typography variant="caption">Labels</Typography>
                        </Tooltip>
                      }
                      sx={{ m: 0 }}
                    />
                    <Button
                      size="small"
                      onClick={() => setCameraFiltersOpen((v) => !v)}
                      disabled={!showCameras}
                      endIcon={cameraFiltersOpen ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
                      sx={{ ml: 'auto', textTransform: 'none', fontSize: '0.7rem', py: 0 }}
                      aria-expanded={cameraFiltersOpen}
                    >
                      Filters
                      {showCameras && (
                        <Typography
                          component="span"
                          variant="caption"
                          color={cameraFiltersActive ? 'primary.main' : 'text.disabled'}
                          sx={{ ml: 0.5 }}
                        >
                          {cameraControls.shownCount.toLocaleString()} / {cameraControls.overlay.totalCount.toLocaleString()}
                        </Typography>
                      )}
                    </Button>
                  </>
                )}
              </Box>
              {cameraControls?.proximity && (
                <Box sx={{ mt: 0.5 }}>
                  <CameraProximityRow proximity={cameraControls.proximity} disabled={!showCameras} />
                </Box>
              )}
              {cameraControls && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <Typography variant="caption" color={showCameras ? 'text.secondary' : 'text.disabled'}>
                    Marker size
                  </Typography>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={cameraControls.overlay.display.size}
                    disabled={!showCameras}
                    onChange={(_, v: CameraMarkerSize | null) => v && cameraControls.overlay.setDisplay({ size: v })}
                    sx={{ '& .MuiToggleButton-root': { px: 1, py: 0.25, fontSize: '0.65rem', textTransform: 'none' } }}
                    aria-label="Camera marker size"
                  >
                    <ToggleButton value="small">Small</ToggleButton>
                    <ToggleButton value="medium">Medium</ToggleButton>
                    <ToggleButton value="large">Large</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              )}
              {cameraControls && (
                <Collapse in={cameraFiltersOpen && showCameras} timeout="auto" unmountOnExit>
                  <Box sx={{ mt: 0.75, p: 1, borderRadius: 1, bgcolor: 'action.hover' }}>
                    <CameraLayerFilterPanel overlay={cameraControls.overlay} hideHeader hideDisplay />
                  </Box>
                </Collapse>
              )}
            </Box>
          </>
        )}

        <Divider sx={{ my: 1 }} />

        {/* Admin reference layers — boundaries, districts, infrastructure, POIs.
            Collapsible (like camera filters): the list can get long. */}
        <Box sx={{ mb: 1.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.4 }}>
              REFERENCE LAYERS
              {referenceLayers.layers.length > 0 && (
                <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 0.5, fontWeight: 400 }}>
                  ({referenceLayers.enabledCount}/{referenceLayers.layers.length} on)
                </Typography>
              )}
            </Typography>
            {referenceLayersOpen && referenceLayers.layers.length > 1 && (
              <Box sx={{ ml: 1, display: 'flex', gap: 0.5 }}>
                <Button
                  size="small"
                  onClick={() => referenceLayers.setAllEnabled(true)}
                  sx={{ minWidth: 0, px: 0.5, py: 0, fontSize: '0.66rem', textTransform: 'none' }}
                >
                  All
                </Button>
                <Button
                  size="small"
                  onClick={() => referenceLayers.setAllEnabled(false)}
                  sx={{ minWidth: 0, px: 0.5, py: 0, fontSize: '0.66rem', textTransform: 'none' }}
                >
                  None
                </Button>
              </Box>
            )}
            <Button
              size="small"
              onClick={() => setReferenceLayersOpen((v) => !v)}
              endIcon={referenceLayersOpen ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
              sx={{ ml: 'auto', textTransform: 'none', fontSize: '0.7rem', py: 0 }}
              aria-expanded={referenceLayersOpen}
            >
              Layers
            </Button>
          </Box>
          <Collapse in={referenceLayersOpen} timeout="auto" unmountOnExit>
            <Box sx={{ mt: 0.75, p: 1, borderRadius: 1, bgcolor: 'action.hover' }}>
              <ReferenceLayersSection controller={referenceLayers} onZoomTo={onZoomToBounds} isAdmin={isAdmin} hideTitle />
            </Box>
          </Collapse>
        </Box>
        </>
        )}

        {!playbackOnly && <Divider sx={{ my: 1 }} />}

        {/* Playback Settings — was a Popover anchored to the slider gear,
            but the popover obscured its own anchor and made closing it
            awkward. Folded in here so there's one panel for everything.
            White Cell sampling and the org-defaults reset live here too,
            since they are track filters applied alongside playback. */}
        <Box>
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5, letterSpacing: 0.4 }}
          >
            TIMESLIDER PLAYBACK
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
            <FormControl size="small" sx={{ minWidth: 100, flex: '1 1 auto' }}>
              <InputLabel sx={{ fontSize: '0.75rem' }}>Unit</InputLabel>
              <Select
                value={timeUnit}
                onChange={(e) => onTimeUnitChange?.(e.target.value as string)}
                label="Unit"
                sx={{ height: 34, fontSize: '0.8rem' }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 220 } } }}
              >
                {TIME_UNITS.map((u) => (
                  <MenuItem key={u.value} value={u.value} sx={{ fontSize: '0.8rem' }}>
                    {u.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 80, flex: '1 1 auto' }}>
              <InputLabel sx={{ fontSize: '0.75rem' }}>Int</InputLabel>
              <Select
                value={intervalValue}
                onChange={(e) => onIntervalValueChange?.(Number(e.target.value))}
                label="Int"
                sx={{ height: 34, fontSize: '0.8rem' }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 220 } } }}
              >
                {INTERVAL_OPTIONS.map((n) => (
                  <MenuItem key={n} value={n} sx={{ fontSize: '0.8rem' }}>
                    {n}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 100, flex: '1 1 auto' }}>
              <InputLabel sx={{ fontSize: '0.75rem' }}>Rate</InputLabel>
              <Select
                value={playRate}
                onChange={(e) => onPlayRateChange?.(Number(e.target.value))}
                label="Rate"
                sx={{ height: 34, fontSize: '0.8rem' }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 220 } } }}
              >
                {PLAY_RATES.map((r) => (
                  <MenuItem key={r} value={r} sx={{ fontSize: '0.8rem' }}>
                    {r}s
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* All five toggles on one line (sm+) so Implausible sits with the rest. */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(3, 1fr)', sm: 'repeat(5, auto)' },
              justifyContent: { sm: 'space-between' },
              columnGap: 1,
              rowGap: 0.25,
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={loop}
                  onChange={(e) => onLoopChange?.(e.target.checked)}
                  size="small"
                />
              }
              label={<Typography variant="caption">Loop</Typography>}
              sx={{ m: 0 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={followMode}
                  onChange={(e) => onFollowModeChange?.(e.target.checked)}
                  size="small"
                  color="secondary"
                />
              }
              label={<Typography variant="caption">Follow</Typography>}
              sx={{ m: 0 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={skipDwells}
                  onChange={(e) => onSkipDwellsChange?.(e.target.checked)}
                  size="small"
                  color="warning"
                />
              }
              label={<Typography variant="caption">Skip Dwell</Typography>}
              sx={{ m: 0 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={hideGaps}
                  onChange={(e) => onHideGapsChange?.(e.target.checked)}
                  size="small"
                  color="info"
                />
              }
              label={
                <Tooltip title="Breaks the track where the device went offline removing the straight line across ground it never traveled.">
                  <Typography variant="caption">GPS Gaps</Typography>
                </Tooltip>
              }
              sx={{ m: 0 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={hideImplausibleMovement}
                  onChange={(e) => onHideImplausibleMovementChange?.(e.target.checked)}
                  size="small"
                  color="error"
                />
              }
              label={
                <Tooltip title={`Drops the spikes where a bad fix jumped the device miles away and back (set by max hop speed). ${implausibleMaxSpeedKmh} km/h (~${mph} mph)`}>
                  <Typography variant="caption">Implausible</Typography>
                </Tooltip>
              }
              sx={{ m: 0 }}
            />
          </Box>

          {hideImplausibleMovement && (
            <Box sx={{ mt: 1.5, px: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                Max hop speed: {implausibleMaxSpeedKmh} km/h (~{mph} mph)
              </Typography>
              <Slider
                size="small"
                value={implausibleMaxSpeedKmh}
                min={80}
                max={300}
                step={10}
                marks={[
                  { value: 120, label: '120' },
                  { value: 160, label: '160' },
                  { value: 250, label: '250' },
                ]}
                onChange={(_, v) => onImplausibleMaxSpeedKmhChange?.(v as number)}
                valueLabelDisplay="auto"
                sx={{ mt: 0.5 }}
              />
            </Box>
          )}

          {/* White Cell sampling + org defaults on one line. */}
          <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel sx={{ fontSize: '0.75rem' }}>White Cell</InputLabel>
              <Select
                value={whiteCellInterval ?? 'off'}
                onChange={(e) => {
                  const value = e.target.value;
                  const interval = value === 'off' ? null : Number(value);
                  onWhiteCellIntervalChange?.(interval);

                  // Auto-enable recommended layers when White Cell is activated
                  // so the sampled points are actually visible. Mirrors the
                  // original behavior from CompactTimeSlider.
                  if (interval !== null) {
                    onShowDataPointsChange?.(true);
                    onShowRouteLinesChange?.(true);
                    onShowDirectionArrowsChange?.(true);
                  }
                }}
                label="White Cell"
                sx={{ height: 34, fontSize: '0.8rem' }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 220 } } }}
              >
                <MenuItem value="off" sx={{ fontSize: '0.8rem' }}>Off</MenuItem>
                <MenuItem value={2} sx={{ fontSize: '0.8rem' }}>2 min</MenuItem>
                <MenuItem value={15} sx={{ fontSize: '0.8rem' }}>15 min</MenuItem>
                <MenuItem value={30} sx={{ fontSize: '0.8rem' }}>30 min</MenuItem>
                <MenuItem value={60} sx={{ fontSize: '0.8rem' }}>1 hour</MenuItem>
                <MenuItem value={120} sx={{ fontSize: '0.8rem' }}>2 hours</MenuItem>
              </Select>
            </FormControl>
            <Tooltip
              title="White Cell sampling: show only one GPS point per interval to reduce clutter."
              arrow
              placement="top"
            >
              <InfoIcon sx={{ fontSize: 12, color: 'text.disabled', cursor: 'help' }} />
            </Tooltip>
            {whiteCellInterval && (
              <Typography variant="caption" sx={{ color: 'info.main' }}>
                1 pt / {whiteCellInterval}m
              </Typography>
            )}
            {onUseOrgTrackFilterDefaults && (
              <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                {trackFiltersUserCustomized && (
                  <Typography variant="caption" color="text.secondary">
                    Session override active
                  </Typography>
                )}
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ResetIcon />}
                  onClick={onUseOrgTrackFilterDefaults}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Use org defaults
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default MapLayerControlsDialog;
